import React from 'react';
import { Activity } from 'lucide-react';
import type { NetworkHealthState } from '../hooks/useNetworkHealth';
import './NetworkPingIndicator.css';

export function formatBytesPerSec(bytesPerSec?: number | null): string {
  if (bytesPerSec == null || !Number.isFinite(bytesPerSec) || bytesPerSec <= 0) return '';
  if (bytesPerSec >= 1024 * 1024) return `${(bytesPerSec / (1024 * 1024)).toFixed(1)} MB/s`;
  if (bytesPerSec >= 1024) return `${(bytesPerSec / 1024).toFixed(0)} KB/s`;
  return `${Math.round(bytesPerSec)} B/s`;
}

function qualityLabel(quality: NetworkHealthState['quality']): string {
  switch (quality) {
    case 'good':
      return 'Conexión buena';
    case 'slow':
      return 'Conexión lenta';
    case 'offline':
      return 'Sin conexión';
    default:
      return 'Midiendo red…';
  }
}

export function buildNetworkPingHint(
  health?: Pick<NetworkHealthState, 'quality' | 'pingMs'> | null,
  loadSpeedBytesPerSec?: number | null
): string {
  const q = health?.quality ?? 'unknown';
  const parts: string[] = [qualityLabel(q)];
  if (health?.pingMs != null && Number.isFinite(health.pingMs)) {
    parts.push(`Ping: ${health.pingMs} ms`);
  }
  const speed = formatBytesPerSec(loadSpeedBytesPerSec);
  if (speed) parts.push(`Velocidad de carga: ${speed}`);
  return parts.join(' · ');
}

export interface NetworkPingIndicatorProps {
  health?: Pick<NetworkHealthState, 'quality' | 'pingMs'> | null;
  /** Bytes/s de descarga o carga HLS (opcional). */
  loadSpeedBytesPerSec?: number | null;
  size?: number;
  className?: string;
  /** Si true, muestra el valor del ping junto al icono. */
  showLabel?: boolean;
}

/**
 * Icono de “ping” / salud de red; al pasar el cursor (title + aria-label) muestra ping y velocidad.
 */
export const NetworkPingIndicator: React.FC<NetworkPingIndicatorProps> = ({
  health,
  loadSpeedBytesPerSec,
  size = 14,
  className = '',
  showLabel = false,
}) => {
  const quality = health?.quality ?? 'unknown';
  const hint = buildNetworkPingHint(health, loadSpeedBytesPerSec);
  const label =
    quality === 'offline'
      ? '—'
      : health?.pingMs != null
        ? `${health.pingMs} ms`
        : '…';

  return (
    <span
      className={`network-ping-indicator network-ping-indicator--${quality} ${className}`.trim()}
      title={hint}
      aria-label={hint}
      role="img"
    >
      <Activity size={size} aria-hidden className="network-ping-indicator__icon" />
      {showLabel && <span className="network-ping-indicator__label">{label}</span>}
    </span>
  );
};
